import { ArrowUpRight, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export const ProductCard = ({ product }) => {
  return (
    <Card
      className="group tilt-card glass-panel card-sheen overflow-hidden rounded-[28px] border-white/10"
      data-testid={`product-card-${product.id}`}
    >
      <div className="grid gap-0 xl:grid-cols-[1.24fr_0.76fr]">
        <div className="relative min-h-[360px] overflow-hidden border-b border-white/10 xl:min-h-[540px] xl:border-b-0 xl:border-r">
          <div className="absolute inset-0 bg-slate-950/40" />
          <div className="absolute inset-0 overflow-hidden bg-slate-950/50">
            <img
              src={product.cover_image}
              alt={`${product.title} cover preview`}
              className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.05]"
              data-testid={`product-cover-${product.id}`}
            />
          </div>

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/18 to-slate-950/50" />

          <div className="absolute left-5 right-5 top-5 flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-cyan-300/30 bg-cyan-300/12 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-cyan-50">
              {product.audience}
            </span>
            {product.tags.slice(0, 2).map((tag) => (
              <span
                key={`${product.id}-${tag}`}
                className="rounded-full border border-white/10 bg-slate-950/45 px-3 py-1 text-xs text-slate-200"
                data-testid={`product-tag-${product.id}-${tag.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {tag}
              </span>
            ))}
            <div className="ml-auto inline-flex items-center gap-2 rounded-full border border-white/10 bg-slate-950/55 px-3 py-1 text-xs text-slate-200">
              <PlayCircle className="h-3.5 w-3.5 text-cyan-200" />
              Preview focus
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 xl:p-10">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-200/75">Featured project</p>
            <h3 className="mt-3 max-w-3xl text-3xl font-semibold text-white sm:text-4xl xl:text-5xl" data-testid={`product-title-${product.id}`}>
              {product.title}
            </h3>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-200/90 sm:text-base" data-testid={`product-summary-${product.id}`}>
              {product.summary}
            </p>
          </div>
        </div>

        <CardContent className="flex h-full flex-col justify-between p-6 md:p-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-200/70">Quick project summary</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {product.capabilities.map((capability) => (
                <span
                  key={`${product.id}-${capability}`}
                  className="rounded-full bg-white/5 px-3 py-2 text-sm text-slate-100"
                  data-testid={`product-capability-${product.id}-${capability.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {capability}
                </span>
              ))}
            </div>

            <div className="mt-7 rounded-[24px] border border-white/10 bg-slate-950/55 p-5" data-testid={`product-preview-panel-${product.id}`}>
              <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Preview emphasis</p>
              <p className="mt-3 text-sm leading-7 text-slate-300">
                Showcase layout intentionally keeps copy minimal and pushes the visual preview forward for faster scanning.
              </p>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              asChild
              className="rounded-full bg-cyan-300 px-5 py-6 text-sm font-semibold text-slate-950 hover:bg-cyan-200"
            >
              <a
                href={product.demo_url}
                target="_blank"
                rel="noreferrer"
                data-testid={`product-demo-link-${product.id}`}
              >
                Book demo
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              className="rounded-full border-white/15 bg-transparent px-5 py-6 text-sm text-slate-100 hover:bg-white/5"
            >
              <a href="#contact" data-testid={`product-contact-link-${product.id}`}>
                Ask about this project
              </a>
            </Button>
          </div>
        </CardContent>
      </div>
    </Card>
  );
};